// @ts-strict-ignore
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Flex } from '../..';
import { ApiPosition } from 'phoenix/redux/models';
import { DescriptionLabel, SymbolLabel } from '../Shared/Labels';
import { SecurityCellChart } from '../Shared/SecurityCellChart';
import { SecurityCellLogo } from '../Shared/SecurityCellLogo';
import { SecurityNameSection } from '../Shared/SecurityNameSection';
import style from '../style.module.scss';
import { PositionValue } from './PositionValue';
import { SecondaryRow } from './SecondaryRow';
import { GetSecurityQuoteAction } from 'phoenix/redux/actions';
import { QualifiedId } from 'phoenix/util/QualifiedId';
import { XS } from 'phoenix/xstream/XS';
import { useXstreamDispatch } from 'phoenix/xstream/XstreamProvider';
import { useInView } from 'react-intersection-observer';

export interface FuturePositionCellProps {
    getCached: boolean;
    hideAccountName?: boolean;
    position: ApiPosition;
    streamingNamespace: string;
    symbol: string;
    visible?: boolean;
}

export const FuturePositionCell = React.memo((props: FuturePositionCellProps) => {
    const dispatch = useDispatch();
    const xdispatch = useXstreamDispatch();
    const { streamingNamespace, position, hideAccountName, symbol } = props;

    const { ref, inView } = useInView({ trackVisibility: true, threshold: 0, delay: 400 });
    const [subscription, setSubscription] = useState<string>(null);

    useEffect(() => {
        dispatch(GetSecurityQuoteAction(symbol));
    }, [symbol]);

    useEffect(() => {
        const run = async () => {
            if (!inView) {
                XS.stop(subscription);
                setSubscription(null);
            } else {
                await dispatch(GetSecurityQuoteAction(symbol));

                if (!subscription) {
                    const ticket: string = await xdispatch(XS.Quotes.start(symbol, streamingNamespace));
                    setSubscription(ticket);
                }
            }
        };

        run();
        return () => {
            XS.stop(subscription);
        };
    }, [inView]);

    const [latestChartPercChange, setLatestChartPercChange] = useState(null);
    const [latestChartPrice, setLatestChartPrice] = useState(null);

    const descriptionText = QualifiedId.RemovePrefix(symbol) || position?.description?.toLowerCase() || '';

    const onCrosshairUpdate = useCallback((value?: any, isScrubbing?: boolean) => {
        setLatestChartPercChange(value[symbol]?.chartPercChange);
        setLatestChartPrice(typeof value[symbol] === 'number' ? value[symbol] : value[symbol]?.value);
    }, []);

    return (
        <>
            <SecurityCellLogo symbol={symbol} />
            <Flex ref={ref} column className={style.cellContent}>
                <Flex className={style.mainRow}>
                    <SecurityNameSection>
                        <SymbolLabel tooltipText={QualifiedId.RemovePrefix(symbol)}>{QualifiedId.RemovePrefix(symbol)}</SymbolLabel>
                        <DescriptionLabel>{descriptionText}</DescriptionLabel>
                    </SecurityNameSection>
                    <SecurityCellChart symbol={symbol} onCrosshairUpdate={onCrosshairUpdate} />
                    <PositionValue chartPercChange={latestChartPercChange} chartPrice={latestChartPrice} position={position} symbol={symbol} withQuoteOverride={true} />
                </Flex>
                <SecondaryRow hideAccountName={hideAccountName} position={position} secType='future' />
            </Flex>
        </>
    );
});
